import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Shield, Mail } from 'lucide-react';
import { initRecaptcha, executeRecaptcha, unloadRecaptcha, isDevEnvironment } from '../lib/recaptcha';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import Cal, { getCalApi } from "@calcom/embed-react";
import { TermsOfServiceModal } from '../components/legal/TermsOfServiceModal';
import { PrivacyPolicyModal } from '../components/legal/PrivacyPolicyModal';
import { NotionTypingInput } from '../components/ui/notion/NotionTypingInput';
import { RegisterSplitLayout } from '../components/register/RegisterSplitLayout';
import { REGISTER_VISUALS } from '../components/register/registerVisuals';
import { TERMS_VERSION, TERMS_ACCEPTANCE_STORAGE_KEY } from '../lib/config/termsVersion';
import { VEXATIOUS_LABEL, VEXATIOUS_DETAIL, VEXATIOUS_LABEL_EN, VEXATIOUS_DETAIL_EN } from '../lib/config/vexatiousClauses';
import { getRegistrationResumeUrl } from '../lib/services/registrationResume';
import { collectAttributionData, ensureEventId } from '../lib/attribution/attributionCookie';
import { trackRegisterStepViewed, trackOnboardingEvent } from '../lib/analytics/onboardingEvents';

// Translations for registration form (solo le chiavi ancora usate dall'entry minimal)
const translations = {
  it: {
    title: 'Inizia ora gratuitamente',
    subtitle: '30 giorni di prova gratuita, nessuna carta richiesta',
    email: 'Email aziendale',
    submit: 'Inizia gratis',
    submitting: 'Creazione account...',
    loading: 'Caricamento...',
    termsLink: 'Termini e Condizioni',
    and: 'e la',
    privacyLink: 'Privacy Policy',
    acceptTermsPre: 'Ho letto e accetto i',
    acceptTermsRequired: 'Devi accettare i Termini e Condizioni per procedere',
    socialConsentIntro: 'Un ultimo passaggio: accetta i termini per continuare',
    socialChoiceGoogle: 'Registrazione con Google',
    socialChoiceMicrosoft: 'Registrazione con Microsoft',
    backToForm: 'Indietro',
    vexatiousLabel: VEXATIOUS_LABEL,
    vexatiousDetailToggle: 'vedi dettaglio',
    vexatiousDetailHide: 'nascondi dettaglio',
    vexatiousDetail: VEXATIOUS_DETAIL,
    vexatiousRequired: 'Devi approvare specificamente le clausole indicate per procedere',
    vexatiousOtpNote: 'Le clausole ex artt. 1341-1342 c.c. verranno approvate specificamente al passaggio di verifica email, inserendo il codice ricevuto.',
    hasAccount: 'Hai già un account?',
    loginLink: 'Accedi qui',
    orContinueWith: 'oppure',
    continueWithMicrosoft: 'Registrati con Microsoft',
    microsoftError: 'Errore con Microsoft. Riprova.',
    continueWithGoogle: 'Registrati con Google',
    googleError: 'Errore con Google. Riprova.',
    protectedBy: 'Protetto da reCAPTCHA',
    loadingProtection: 'Caricamento protezioni...',
    emailPlaceholders: ["mario@azienda.it", "info@startup.com", "team@impresa.it"],
    bookDemo: 'Prenota una demo',
    bookDemoSubtitle: 'Scopri Flusia con un nostro consulente',
    emailRequired: 'Email richiesta',
    emailInvalid: 'Email non valida',
    securityNotReady: 'Sistema di sicurezza non pronto. Riprova tra qualche secondo.',
    securityFailed: 'Non siamo riusciti a completare la verifica di sicurezza. Ricarica la pagina e riprova, oppure registrati con Google o Microsoft.',
    emailAlreadyRegistered: 'è già registrata.\n\nVuoi accedere con questa email?',
    registrationError: 'Non siamo riusciti a completare la registrazione. Riprova tra qualche minuto.',
    networkError: 'Connessione non riuscita. Controlla la rete e riprova.',
    tooManyRequests: 'Troppi tentativi. Riprova tra qualche minuto.'
  },
  en: {
    title: 'Start for free',
    subtitle: '30-day free trial, no credit card required',
    email: 'Business Email',
    submit: 'Start for free',
    submitting: 'Creating account...',
    loading: 'Loading...',
    termsLink: 'Terms of Service',
    and: 'and',
    privacyLink: 'Privacy Policy',
    acceptTermsPre: 'I have read and accept the',
    acceptTermsRequired: 'You must accept the Terms of Service to proceed',
    socialConsentIntro: 'One last step: accept the terms to continue',
    socialChoiceGoogle: 'Signing up with Google',
    socialChoiceMicrosoft: 'Signing up with Microsoft',
    backToForm: 'Back',
    vexatiousLabel: VEXATIOUS_LABEL_EN,
    vexatiousDetailToggle: 'view details',
    vexatiousDetailHide: 'hide details',
    vexatiousDetail: VEXATIOUS_DETAIL_EN,
    vexatiousRequired: 'You must specifically approve the listed clauses to proceed',
    vexatiousOtpNote: 'The clauses under Articles 1341-1342 of the Italian Civil Code will be specifically approved at the email verification step, by entering the received code.',
    hasAccount: 'Already have an account?',
    loginLink: 'Sign in here',
    orContinueWith: 'or',
    continueWithMicrosoft: 'Sign up with Microsoft',
    microsoftError: 'Error with Microsoft. Please try again.',
    continueWithGoogle: 'Sign up with Google',
    googleError: 'Error with Google. Please try again.',
    protectedBy: 'Protected by reCAPTCHA',
    loadingProtection: 'Loading protection...',
    emailPlaceholders: ["john@company.com", "info@startup.com", "team@business.com"],
    bookDemo: 'Book a demo',
    bookDemoSubtitle: 'Discover Flusia with one of our consultants',
    emailRequired: 'Email required',
    emailInvalid: 'Invalid email',
    securityNotReady: 'Security system not ready. Please try again in a moment.',
    securityFailed: 'We could not complete the security check. Reload the page and try again, or sign up with Google or Microsoft.',
    emailAlreadyRegistered: 'is already registered.\n\nWould you like to sign in with this email?',
    registrationError: 'We could not complete your registration. Please try again in a few minutes.',
    networkError: 'Connection failed. Check your network and try again.',
    tooManyRequests: 'Too many attempts. Please try again in a few minutes.'
  }
};

type RegisterTexts = typeof translations.it;

// Errore gia' scritto per l'utente: il catch di handleSubmit lo mostra cosi' com'e'.
class RegisterUserError extends Error {}

// Testo leggibile per una risposta non 2xx di register-start. supabase-js mette
// il body in error.context e in error.message una frase tecnica ("Edge Function
// returned a non-2xx status code") che non va mai mostrata.
async function registerStartErrorMessage(error: any, t: RegisterTexts, language: 'it' | 'en'): Promise<string> {
  const ctx = error?.context;
  if (!ctx || typeof ctx.clone !== 'function') return t.networkError;
  const status: number = ctx.status;
  const body = await ctx.clone().json().catch(() => null);
  const serverMessage: string = typeof body?.error === 'string' ? body.error : '';
  if (status === 429) return t.tooManyRequests;
  if (/verifica di sicurezza/i.test(serverMessage)) return t.securityFailed;
  // I 400 di register-start sono gia' frasi per l'utente (email temporanea,
  // dominio non valido, codice partner), ma solo in italiano.
  if (status === 400 && serverMessage && language === 'it') return serverMessage;
  return t.registrationError;
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref') || '';
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [oauthLoading, setOauthLoading] = useState<'azure' | 'google' | null>(null);
  // Click su Google/Microsoft senza consensi: invece dell'errore rosso in
  // fondo, il form sotto collassa e le checkbox salgono qui (animato).
  const [pendingSocial, setPendingSocial] = useState<'azure' | 'google' | null>(null);
  const { signInWithOAuth } = useAuth();
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  // Accettazione T&C: checkbox esplicita + approvazione specifica clausole
  // vessatorie ex artt. 1341-1342 c.c. Entrambe obbligatorie (anche per signup social).
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [vexatiousAccepted, setVexatiousAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [showVexatiousDetail, setShowVexatiousDetail] = useState(false);
  const [language, setLanguage] = useState<'it' | 'en'>(() => {
    const browserLang = navigator.language || (navigator as any).languages?.[0] || 'en';
    return browserLang.toLowerCase().startsWith('it') ? 'it' : 'en';
  });

  // Get current translations
  const t = translations[language];

  // Chiave reCAPTCHA
  const recaptchaSiteKey = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY ||
                          '6LdR1x0rAAAAAEaN_aDN9JSeAg7tYFssz_S_6ag7';

  // Verifica se la registrazione è abilitata
  const isRegistrationEnabled = import.meta.env.VITE_REGISTRATION_ENABLED !== 'false';

  // Controlla se l'utente è già loggato. Con l'auto-login post-password, un
  // utente che abbandona la registrazione a metà ha comunque una sessione
  // valida: se rimbalzassimo sempre a '/' finirebbe su una dashboard rotta
  // senza permessi e senza via di rientro. Discriminatore "registrazione
  // completa" = esiste almeno un ruolo in user_roles (assegnato SOLO da
  // register-complete / gestione utenti). NB: organization_id e
  // subscription_status NON discriminano: register-password crea subito l'org
  // placeholder e la colonna ha DEFAULT 'trial'. Registrazione a metà →
  // restiamo sull'entry: l'utente reinserisce l'email e register-start lo
  // riprende dallo step giusto (resume).
  useEffect(() => { trackRegisterStepViewed('entry'); }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          const { count, error: rolesError } = await supabase
            .from('user_roles')
            .select('user_id', { count: 'exact', head: true })
            .eq('user_id', session.user.id);
          if (rolesError) {
            // Impossibile determinare lo stato: manteniamo il comportamento
            // storico per non impattare gli utenti normali già registrati.
            navigate('/');
            return;
          }
          if ((count ?? 0) > 0) {
            navigate('/');
            return;
          }
          // Nessun ruolo → registrazione a metà: riprendi direttamente il
          // wizard (la edge riusa registration_session e org placeholder).
          // Se la edge non risponde, fallback storico: entry visibile.
          const resumeUrl = await getRegistrationResumeUrl();
          if (resumeUrl) {
            window.location.replace(resumeUrl);
            return;
          }
        }
      } catch (error) {
        console.error('Error checking auth:', error);
      }
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate]);

  // Inizializza reCAPTCHA (solo se registrazione abilitata)
  useEffect(() => {
    if (!isRegistrationEnabled) return;

    const loadRecaptcha = async () => {
      try {
        await initRecaptcha(recaptchaSiteKey);
        setRecaptchaReady(true);
      } catch (error) {
        console.error('Failed to initialize reCAPTCHA:', error);
        if (isDevEnvironment()) {
          setRecaptchaReady(true);
        }
      }
    };

    loadRecaptcha();

    return () => {
      unloadRecaptcha();
    };
  }, [recaptchaSiteKey, isRegistrationEnabled]);

  // Configura Cal.com UI quando la registrazione è disabilitata
  useEffect(() => {
    if (isRegistrationEnabled) return;

    (async function () {
      const cal = await getCalApi({ namespace: "30min" });
      cal("ui", {
        cssVarsPerTheme: {
          light: { "cal-brand": "#1F2937" },
          dark: { "cal-brand": "#fafafa" }
        },
        hideEventTypeDetails: false,
        layout: "month_view"
      });
    })();
  }, [isRegistrationEnabled]);

  const validateEmail = (): boolean => {
    if (!email.trim()) {
      setEmailError(t.emailRequired);
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError(t.emailInvalid);
      return false;
    }
    setEmailError(null);
    return true;
  };

  // Verifica accettazione T&C. La spunta vessatorie è richiesta SOLO nel
  // flusso social: nel flusso email l'approvazione specifica avviene con
  // l'OTP di verifica (firma elettronica semplice, Cass. 20945/2026) e qui
  // la checkbox non viene nemmeno mostrata.
  const validateTermsAcceptance = (requireVexatious = true): boolean => {
    if (!termsAccepted) {
      setTermsError(t.acceptTermsRequired);
      return false;
    }
    if (requireVexatious && !vexatiousAccepted) {
      setTermsError(t.vexatiousRequired);
      return false;
    }
    setTermsError(null);
    return true;
  };

  // Salva l'accettazione in sessionStorage prima del redirect OAuth: verrà
  // letta da OAuthCallback e passata a register-oauth-start per il log a DB.
  const persistTermsAcceptanceForOAuth = () => {
    try {
      sessionStorage.setItem(TERMS_ACCEPTANCE_STORAGE_KEY, JSON.stringify({
        version: TERMS_VERSION,
        vexatiousAccepted: true,
        acceptedAt: new Date().toISOString()
      }));
    } catch (e) {
      console.warn('Unable to persist terms acceptance for OAuth flow:', e);
    }
  };

  // Avvio OAuth vero e proprio (consensi già validati dal chiamante)
  const startSocial = async (provider: 'google' | 'azure') => {
    persistTermsAcceptanceForOAuth();
    setOauthLoading(provider);
    setOauthError(null);
    const result = await signInWithOAuth(provider);
    if (result.error) {
      setOauthError(result.error.message || (provider === 'google' ? t.googleError : t.microsoftError));
      setOauthLoading(null);
    }
  };

  // Click sul bottone social: se mancano i consensi NON mostriamo l'errore
  // rosso in fondo — collassiamo il form email e portiamo su le checkbox
  // (pannello pendingSocial), poi si continua da lì.
  const handleSocialClick = (provider: 'google' | 'azure') => {
    if (!termsAccepted || !vexatiousAccepted) {
      setTermsError(null);
      setPendingSocial(provider);
      return;
    }
    startSocial(provider);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail()) return;
    // Flusso email: la vessatorie NON è richiesta qui — la approva l'OTP.
    if (!validateTermsAcceptance(false)) return;

    if (!isDevEnvironment() && !recaptchaReady) {
      alert(t.securityNotReady);
      return;
    }

    setIsSubmitting(true);
    try {
      let recaptchaToken = '';

      if (!isDevEnvironment()) {
        try {
          // Il token va verificato UNA sola volta da Google: lo fa register-start.
          // Verificarlo prima qui (verify-recaptcha) lo consumava e il server lo
          // rifiutava come duplicato.
          recaptchaToken = await executeRecaptcha('register');
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          // Don't block — the raw token from executeRecaptcha is still sent to the backend
        }
      }

      const attributionData = ensureEventId(collectAttributionData());

      const { data, error } = await supabase.functions.invoke('register-start', {
        body: {
          email,
          referralCode: referralCode || null,
          recaptchaToken,
          language,
          website: honeypot,
          termsAccepted: true,
          // Stato reale: nel flusso email è false — l'approvazione specifica
          // arriva con l'OTP e viene registrata in legal_acceptance.vexatious.
          vexatiousAccepted,
          termsVersion: TERMS_VERSION,
          attributionData
        }
      });

      if (error) {
        throw new RegisterUserError(await registerStartErrorMessage(error, t, language));
      }

      if (!data.error) {
        trackOnboardingEvent('flusia.register.started');
      }

      if (data.error) {
        if (data.userExists) {
          const message = `"${email}" ${t.emailAlreadyRegistered}`;
          const goToLogin = confirm(message);
          if (goToLogin) {
            window.location.href = `/login?email=${encodeURIComponent(email)}`;
            return;
          } else {
            const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
            if (emailInput) {
              emailInput.focus();
              emailInput.select();
            }
          }
        } else {
          throw new RegisterUserError(data.error || t.registrationError);
        }
        return;
      }

      // Check if resuming an incomplete registration
      if (data.resumeStep) {
        // Step 3 = password fatta, mancano profilo+azienda. Lo step Profilo
        // scrive su user_profiles e richiede la sessione auth (auto-login
        // post-password, stesso device): senza sessione (resume da altro
        // device) si salta ad Azienda, che lavora solo col token.
        const { data: { session: authSession } } = await supabase.auth.getSession();
        const stepRoutes: Record<number, string> = {
          1: `/register/verify?token=${data.sessionToken}`,
          2: `/register/password?token=${data.sessionToken}`,
          3: authSession
            ? `/register/profile?token=${data.sessionToken}`
            : `/register/company?token=${data.sessionToken}`,
          4: `/register/complete?token=${data.sessionToken}`,
          5: `/register/complete?token=${data.sessionToken}`,
        };
        const route = stepRoutes[data.resumeStep] || `/register/verify?token=${data.sessionToken}`;
        window.location.href = route;
        return;
      }

      window.location.href = `/register/verify?token=${data.sessionToken}`;

    } catch (error) {
      console.error('Registration error:', error);
      alert(error instanceof RegisterUserError ? error.message : t.registrationError);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading auth check: spinner semplice su bianco (NON notion-register-bg scuro)
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-2 text-[#787774]">
          <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
          <span>{t.loading}</span>
        </div>
      </div>
    );
  }

  // Checkbox legali (T&C + clausole vessatorie): stesso blocco usato nel form
  // email E nel pannello consensi che appare al click social senza spunte.
  const legalCheckboxes = (
    <div className="space-y-2.5">
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={termsAccepted}
          onChange={(e) => {
            setTermsAccepted(e.target.checked);
            if (e.target.checked) setTermsError(null);
          }}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-[rgba(55,53,47,0.3)] text-[#2383E2] focus:ring-[#2383E2] cursor-pointer"
        />
        <span className="text-xs text-[#787774] leading-relaxed">
          {t.acceptTermsPre}{' '}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}
            className="text-[#2383E2] hover:underline"
          >
            {t.termsLink}
          </button>{' '}
          {t.and}{' '}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setShowPrivacyModal(true); }}
            className="text-[#2383E2] hover:underline"
          >
            {t.privacyLink}
          </button>
        </span>
      </label>

      {/* Vessatorie: checkbox SOLO nel pannello social (che non passa dall'OTP).
          Nel flusso email l'approvazione specifica avviene inserendo il codice
          di verifica (VexatiousApprovalNotice in VerifyEmailPage) — qui resta
          solo una nota informativa. */}
      {pendingSocial ? (
        <div>
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={vexatiousAccepted}
              onChange={(e) => {
                setVexatiousAccepted(e.target.checked);
                if (e.target.checked) setTermsError(null);
              }}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-[rgba(55,53,47,0.3)] text-[#2383E2] focus:ring-[#2383E2] cursor-pointer"
            />
            <span className="grid text-xs text-[#787774] leading-relaxed">
              {/* Sizer invisibile con l'altra lingua: riserva sempre l'altezza
                  massima, così lo switch IT/EN non sposta il form centrato */}
              <span aria-hidden="true" className="invisible [grid-area:1/1]">
                {translations[language === 'it' ? 'en' : 'it'].vexatiousLabel}{' '}
                ({translations[language === 'it' ? 'en' : 'it'].vexatiousDetailToggle})
              </span>
              <span className="[grid-area:1/1]">
                {t.vexatiousLabel}{' '}
                (<button
                  type="button"
                  onClick={(e) => { e.preventDefault(); setShowVexatiousDetail(!showVexatiousDetail); }}
                  className="text-[#2383E2] hover:underline"
                >
                  {showVexatiousDetail ? t.vexatiousDetailHide : t.vexatiousDetailToggle}
                </button>)
              </span>
            </span>
          </label>
          {showVexatiousDetail && (
            <p className="mt-1.5 ml-[26px] text-[11px] text-[#787774] leading-relaxed bg-[rgba(55,53,47,0.03)] border border-[rgba(55,53,47,0.09)] rounded-md px-2.5 py-2">
              {t.vexatiousDetail}
            </p>
          )}
        </div>
      ) : (
        <p className="text-[11px] text-[#9B9A97] leading-relaxed ml-[26px]">
          {t.vexatiousOtpNote}
        </p>
      )}

      {termsError && (
        <p className="text-xs text-red-600">{termsError}</p>
      )}
    </div>
  );

  // Icone provider (riusate nei bottoni social e nel CTA del pannello consensi)
  const googleIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );
  const microsoftIcon = (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#F25022" d="M1 1h10v10H1z"/>
      <path fill="#00A4EF" d="M1 13h10v10H1z"/>
      <path fill="#7FBA00" d="M13 1h10v10H13z"/>
      <path fill="#FFB900" d="M13 13h10v10H13z"/>
    </svg>
  );

  return (
    <>
      <RegisterSplitLayout visual={REGISTER_VISUALS.entry} step={1} language={language}>
        {/* Switcher lingua compatto in alto a destra del form */}
        <div className="flex justify-end mb-4">
          <div className="inline-flex rounded-md border border-[rgba(55,53,47,0.16)] overflow-hidden text-xs">
            <button
              type="button"
              onClick={() => setLanguage('it')}
              className={`px-2.5 py-1 ${language === 'it' ? 'bg-[#37352F] text-white' : 'text-[#787774] hover:bg-[rgba(55,53,47,0.04)]'}`}
            >
              IT
            </button>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              className={`px-2.5 py-1 ${language === 'en' ? 'bg-[#37352F] text-white' : 'text-[#787774] hover:bg-[rgba(55,53,47,0.04)]'}`}
            >
              EN
            </button>
          </div>
        </div>

        {/* key={language}: rimonta con fade quando cambia lingua (niente scatto) */}
        <motion.div
          key={language}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
        {!isRegistrationEnabled ? (
          // Cal.com when registration is disabled
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-[#37352F] mb-2">
                {t.bookDemo}
              </h2>
              <p className="text-sm text-[#787774]">
                {t.bookDemoSubtitle}
              </p>
            </div>

            <Cal
              namespace="30min"
              calLink="luca-bosso-corely/30min"
              style={{ width: "100%", height: "min(600px, 60vh)", overflow: "scroll" }}
              config={{ layout: "month_view" }}
            />

            <div className="text-center mt-4 pt-4 border-t border-[rgba(55,53,47,0.09)]">
              <p className="text-sm text-[#787774]">
                {t.hasAccount}{' '}
                <Link to="/login" className="text-[#2383E2] hover:underline font-medium">
                  {t.loginLink}
                </Link>
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-[#37352F] mb-1">{t.title}</h1>
              <p className="text-sm text-[#787774]">{t.subtitle}</p>
            </div>

            {oauthError && (
              <p className="mb-3 text-xs text-center text-red-600">{oauthError}</p>
            )}

            <AnimatePresence initial={false} mode="wait">
            {pendingSocial ? (
              /* Pannello consensi social: i bottoni provider e il form email
                 collassano; resta la scelta fatta (chip provider), le checkbox
                 e il CTA personalizzato col provider scelto. */
              <motion.div
                key="social-consent"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                transition={{ duration: 0.25 }}
              >
                {/* Scelta fatta: intestazione semplice (niente look da bottone) */}
                <div className="flex items-center gap-2.5 mb-1.5">
                  {pendingSocial === 'google' ? googleIcon : microsoftIcon}
                  <span className="text-base font-semibold text-[#37352F]">
                    {pendingSocial === 'google' ? t.socialChoiceGoogle : t.socialChoiceMicrosoft}
                  </span>
                </div>
                <p className="text-sm text-[#787774] mb-4">
                  {t.socialConsentIntro}
                </p>
                {legalCheckboxes}
                <div className="mt-5 space-y-3">
                  <button
                    type="button"
                    disabled={!!oauthLoading}
                    onClick={() => {
                      if (!validateTermsAcceptance()) return;
                      startSocial(pendingSocial);
                    }}
                    className="notion-register-btn w-full"
                  >
                    {oauthLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        {pendingSocial === 'google' ? googleIcon : microsoftIcon}
                        <span>{pendingSocial === 'google' ? t.continueWithGoogle : t.continueWithMicrosoft}</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPendingSocial(null); setTermsError(null); }}
                    className="w-full text-center text-sm text-[#787774] hover:text-[#37352F] transition-colors py-1.5"
                  >
                    {t.backToForm}
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="email-form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
            {/* Signup social (Google/Microsoft) — go-live 16 lug 2026, gating via env VITE_OAUTH_* (vedi MD/integrations/google-sso.md e microsoft-sso.md) */}
            {(import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true' || import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true') && (
              <div className="mb-5">
                {import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true' && (
                  <button
                    type="button"
                    disabled={!!oauthLoading || isSubmitting}
                    onClick={() => handleSocialClick('google')}
                    className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                  >
                    {oauthLoading === 'google' ? (
                      <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                    ) : googleIcon}
                    {t.continueWithGoogle}
                  </button>
                )}

                {import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true' && (
                  <button
                    type="button"
                    disabled={!!oauthLoading || isSubmitting}
                    onClick={() => handleSocialClick('azure')}
                    className="mt-3 w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border border-[rgba(55,53,47,0.16)] bg-white hover:bg-[rgba(55,53,47,0.03)] text-[#37352F] text-sm font-medium transition-all duration-200 disabled:opacity-50"
                  >
                    {oauthLoading === 'azure' ? (
                      <div className="w-5 h-5 border-2 border-[rgba(55,53,47,0.2)] border-t-[#37352F] rounded-full animate-spin" />
                    ) : microsoftIcon}
                    {t.continueWithMicrosoft}
                  </button>
                )}
              </div>
            )}
            {(import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true' || import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true') && (
              <div className="relative mb-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[rgba(55,53,47,0.09)]" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-[#787774] bg-white">{t.orContinueWith}</span>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - invisible to humans, filled by bots */}
              <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              <NotionTypingInput
                label={t.email}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholders={t.emailPlaceholders}
                icon={<Mail className="w-4 h-4" />}
                error={emailError}
                required
              />

              <button
                type="submit"
                disabled={isSubmitting || (!isDevEnvironment() && !recaptchaReady)}
                className="notion-register-btn w-full"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    <span>{t.submitting}</span>
                  </>
                ) : !isDevEnvironment() && !recaptchaReady ? (
                  <>
                    <Shield className="w-4 h-4 animate-pulse" />
                    <span>{t.loading}</span>
                  </>
                ) : (
                  <>
                    <span>{t.submit}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              {/* Accettazione T&C: checkbox esplicita + doppia approvazione
                  clausole vessatorie (artt. 1341-1342 c.c.). Obbligatorie
                  anche per la registrazione via Google/Microsoft. */}
              {legalCheckboxes}

              {/* reCAPTCHA badge */}
              <div className="text-center pt-2">
                {!isDevEnvironment() && (
                  <div className="flex items-center justify-center gap-1.5 mt-3">
                    <Shield className={`w-3.5 h-3.5 ${recaptchaReady ? 'text-[#0F7B6C]' : 'text-[#787774]'}`} />
                    <span className="text-xs text-[#787774]">
                      {recaptchaReady ? t.protectedBy : t.loadingProtection}
                    </span>
                  </div>
                )}
              </div>
            </form>

            <p className="mt-6 pt-6 border-t border-[rgba(55,53,47,0.09)] text-sm text-[#787774] text-center">
              {t.hasAccount}{' '}
              <Link to="/login" className="text-[#2383E2] hover:underline font-medium">{t.loginLink}</Link>
            </p>
              </motion.div>
            )}
            </AnimatePresence>
          </>
        )}
        </motion.div>
      </RegisterSplitLayout>

      {/* Legal Modals */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
      <PrivacyPolicyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
      />
    </>
  );
}
